import { useCallback, useRef, type CSSProperties as ReactCSSProperties } from 'react';
import {
  Center,
  Column,
  CustomSpinner,
  EmptyScreen,
  SortingArrow,
  Text,
  type TSortValue,
} from '~/commonComponents';
import { ICandidateListViewEntity } from '~/domain/PermanentJobs';
import { useToggle } from '~/hooks';
import CandidateEmptyScreenIcon from '~/images/candidate-empty-screen-icon.svg';
import { Colors } from '~/themes/colors';
import { HirePopupConfirmation } from './HirePopup';
import { ListItem } from './ListItem';
import { RejectPopupConfirmation } from './RejectPopup';

export const Styles = {
  Table: {
    borderSpacing: '0 12px',
    borderCollapse: 'separate',
  } as ReactCSSProperties,
  TableHeader: {
    ActionCol: {
      paddingLeft: 40,
      textAlign: 'left',
      width: '25%',
    } as ReactCSSProperties,
    AvatarCol: {
      textAlign: 'left',
      width: 96,
    } as ReactCSSProperties,
    CandidateCol: {
      textAlign: 'left',
    } as ReactCSSProperties,
    ProcessStepCol: {
      textAlign: 'left',
      width: '15%',
    } as ReactCSSProperties,
    ShortlistCol: {
      textAlign: 'left',
      width: '12%',
    } as ReactCSSProperties,
  },
};

interface ICandidateListTableProps {
  data: ICandidateListViewEntity[];
  isLoading: boolean;
  sorting: TSortValue;
  onSortingChange?: (value: TSortValue | undefined) => void;
}

export const CandidateListTable = ({
  data,
  isLoading,
  sorting,
  onSortingChange,
}: ICandidateListTableProps) => {
  const [isHireOpen, controlsHire] = useToggle();
  const [isRejectOpen, controlsReject] = useToggle();
  const targetedProfessional = useRef<ICandidateListViewEntity['professional'] | undefined>(
    undefined,
  );

  const isEmptyData = data.length === 0;

  const handleHireClicked = useCallback(
    (professional: ICandidateListViewEntity['professional']) => {
      targetedProfessional.current = professional;
      controlsHire.setOn();
    },
    [controlsHire],
  );

  const handleRejectClicked = useCallback(
    (professional: ICandidateListViewEntity['professional']) => {
      targetedProfessional.current = professional;
      controlsReject.setOn();
    },
    [controlsReject],
  );

  if (!isLoading && isEmptyData) {
    return (
      <EmptyScreen
        Icon={() => (
          <img
            alt="candidate-empty-screen-icon"
            src={CandidateEmptyScreenIcon}
            height={170}
            width={170}
          />
        )}
        title="No candidates yet"
        description={
          <Column alignItems="center" gap={48} marginBottom={146}>
            <Text color={Colors.neutral_500}>
              You haven’t received any applications for this position.
              <br />
              To attract candidates, make sure you’ve posted
              <br />
              enough onsite interviews; this is how dental
              <br />
              professionals apply to your job.
            </Text>
          </Column>
        }
      />
    );
  }

  return (
    <>
      <table style={Styles.Table}>
        <thead>
          <tr>
            <th style={Styles.TableHeader.AvatarCol} />
            <th style={Styles.TableHeader.CandidateCol}>
              <Text bold color={Colors.primary_500} fontSize={18}>
                Candidates
              </Text>
            </th>
            <th style={Styles.TableHeader.ProcessStepCol}>
              <SortingArrow
                name="status"
                sorting={sorting}
                style={{ justifyContent: 'flex-start' }}
                variant="primary"
                onChange={onSortingChange}
              >
                <Text bold color={Colors.primary_500} fontSize={18}>
                  Process Step
                </Text>
              </SortingArrow>
            </th>
            <th style={Styles.TableHeader.ShortlistCol}>
              <SortingArrow
                name="shortlist"
                sorting={sorting}
                variant="primary"
                onChange={onSortingChange}
              >
                <Text bold color={Colors.primary_500} fontSize={18}>
                  Shortlist
                </Text>
              </SortingArrow>
            </th>
            <th style={Styles.TableHeader.ActionCol}>
              <Text bold color={Colors.primary_500} fontSize={18}>
                Actions
              </Text>
            </th>
          </tr>
        </thead>
        <tbody>
          {!isLoading &&
            data.map((it) => (
              <tr key={it.id}>
                <td colSpan={5}>
                  <ListItem
                    data={it}
                    onHireClick={handleHireClicked}
                    onRejectClick={handleRejectClicked}
                  />
                </td>
              </tr>
            ))}
        </tbody>
      </table>
      {isLoading && (
        <Center marginBlock={32}>
          <CustomSpinner isLoading size={32} />
        </Center>
      )}
      {isHireOpen && targetedProfessional.current && (
        <HirePopupConfirmation
          controls={controlsHire}
          professionalId={targetedProfessional.current.id}
          professionalName={targetedProfessional.current.name}
        />
      )}
      {isRejectOpen && targetedProfessional.current && (
        <RejectPopupConfirmation
          controls={controlsReject}
          professionalId={targetedProfessional.current.id}
          professionalName={targetedProfessional.current.name}
        />
      )}
    </>
  );
};
